/**
 * "Schedule an interpreter request for later" from inside a native call
 * (plan #75; web: vrs-room-controls.js createScheduleButton).
 *
 * Server contract: request_interpreter { schedule: true, scheduledStartAt }
 * is answered with request_scheduled { requestId, scheduledStartAt, reused }
 * or an error with operation 'request_interpreter'. One scheduled request per
 * account; asking again returns the existing one (reused), which is also the
 * only way to learn about it after a restart. cancel_request { requestId } is
 * answered with request_cancelled { scheduled: true }, or a plain error
 * "Queue request not found." once it has gone live or is gone. At its time the
 * server queues it (request_queued { scheduled: true }).
 *
 * Never through the redux requestInterpreter action: that clears the match
 * data of the call this person is in. The shared requestCancelled listener
 * skips scheduled cancellations for the same reason (middleware.ts).
 *
 * State lives here, outside the toolbar and the More sheet, which close on
 * press; the sheet and the row read it.
 */
import { queueService } from './InterpreterQueueService';
import { p2pInterpreterRequestTarget } from './p2pInterpreterRequest';
/** The server refuses a time under 90 s away; the picker asks for 2 minutes. */
export const SCHEDULE_MIN_LEAD_MS = 2 * 60 * 1000;
export const SCHEDULE_MAX_LEAD_MS = 30 * 24 * 60 * 60 * 1000;
export const SCHEDULE_DEFAULT_LEAD_MS = 30 * 60 * 1000;
/** How long to wait for the server before saying so. */
export const SCHEDULE_ANSWER_TIMEOUT_MS = 15000;
export const SCHEDULE_LABEL = 'Schedule an interpreter';
export const SCHEDULE_ACCESSIBILITY_LABEL = 'Schedule an interpreter request for later';
export const SCHEDULE_BUTTON = 'Schedule';
export const SCHEDULING_STATUS = 'Scheduling…';
export const CANCEL_SCHEDULED_BUTTON = 'Cancel request';
export const KEEP_SCHEDULED_BUTTON = 'Keep';
export const CANCELLING_SCHEDULED_STATUS = 'Cancelling…';
export const SCHEDULE_TIME_OUT_OF_RANGE = 'Pick a time from 2 minutes to 30 days from now.';
export const SCHEDULE_FAILED = 'Could not schedule. Try again.';
export const SCHEDULE_NO_ANSWER = 'No answer. Try again.';
export const SCHEDULE_CALLER_ID_UNAVAILABLE = 'Could not schedule. Check your caller ID.';
export const SCHEDULE_CANCEL_FAILED = 'Could not cancel. Try again.';
export const SCHEDULE_GONE = 'No scheduled request.';
const IDLE = { phase: 'idle' };
let view = IDLE;
let answerTimer;
// Whether the sheet is showing: it says what went wrong itself, so the room's
// notice does not say it twice.
let sheetOpen = false;
let uninstall;
const subscribers = new Set();
function set(next) {
    view = next;
    subscribers.forEach(listener => listener());
}
function clearAnswerTimer() {
    if (answerTimer !== undefined) {
        clearTimeout(answerTimer);
        answerTimer = undefined;
    }
}
function waitForAnswer(onTimeout) {
    clearAnswerTimer();
    answerTimer = setTimeout(() => {
        answerTimer = undefined;
        onTimeout();
    }, SCHEDULE_ANSWER_TIMEOUT_MS);
}
/** Subscribe to changes (the row and the sheet). */
export function subscribeScheduledRequest(listener) {
    subscribers.add(listener);
    return () => {
        subscribers.delete(listener);
    };
}
/** The current state; the same object until it changes. */
export function getScheduledRequestView() {
    return view;
}
/**
 * The picker's first time: 30 minutes from now, on the minute.
 *
 * @param {number} now - Epoch ms.
 * @returns {Date}
 */
export function defaultScheduleTime(now = Date.now()) {
    const when = new Date(now + SCHEDULE_DEFAULT_LEAD_MS);
    when.setSeconds(0, 0);
    return when;
}
/** Whether a time can be scheduled: 2 minutes to 30 days from now. */
export function isSchedulableTime(when, now = Date.now()) {
    const lead = when.getTime() - now;
    return Number.isFinite(lead) && lead >= SCHEDULE_MIN_LEAD_MS && lead <= SCHEDULE_MAX_LEAD_MS;
}
/**
 * "3:30 PM" today, "Sat, Sep 27, 3:30 PM" another day.
 *
 * @param {string} iso - The scheduled time.
 * @param {number} now - Epoch ms.
 * @returns {string}
 */
export function formatScheduledTime(iso, now = Date.now()) {
    const when = new Date(iso || '');
    if (!Number.isFinite(when.getTime())) {
        return '';
    }
    const time = when.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    if (when.toDateString() === new Date(now).toDateString()) {
        return time;
    }
    return `${when.toLocaleDateString([], { day: 'numeric', month: 'short', weekday: 'short' })}, ${time}`;
}
/** The row's words: "Schedule an interpreter", or "Scheduled 3:30 PM" once set. */
export function scheduleRowLabel(current = view, now = Date.now()) {
    const time = formatScheduledTime(current.scheduledStartAt, now);
    return (current.phase === 'scheduled' || current.phase === 'cancelling') && time
        ? `Scheduled ${time}` : SCHEDULE_LABEL;
}
/** Short client copy for a refusal of the schedule (never the server's prose). */
export function scheduleRefusalText(data) {
    const message = typeof data.message === 'string' ? data.message : '';
    if (data.code === 'CALLER_ID_UNAVAILABLE') {
        return SCHEDULE_CALLER_ID_UNAVAILABLE;
    }
    if (/at least 2 minutes|within the next 30 days|valid scheduled time/i.test(message)) {
        return SCHEDULE_TIME_OUT_OF_RANGE;
    }
    return SCHEDULE_FAILED;
}
/**
 * Schedule an interpreter request at this time, in the caller's region and
 * sign language (the same pick as the in-call interpreter button).
 *
 * @returns {boolean} Whether the request went to the queue socket.
 */
export function scheduleInterpreterRequestAt(when, now = Date.now()) {
    if (view.phase === 'scheduling' || view.phase === 'cancelling') {
        return false;
    }
    if (!isSchedulableTime(when, now)) {
        set({ ...view, problem: SCHEDULE_TIME_OUT_OF_RANGE });
        return false;
    }
    const previous = view;
    const target = p2pInterpreterRequestTarget();
    set({ ...view, phase: 'scheduling', problem: undefined });
    waitForAnswer(() => {
        if (view.phase === 'scheduling') {
            set({ ...previous, problem: SCHEDULE_NO_ANSWER });
        }
    });
    queueService.scheduleInterpreterRequest(when.toISOString(), {
        callType: 'vrs',
        language: target.language,
        serviceRegion: target.serviceRegion
    });
    return true;
}
/**
 * Cancel the scheduled request (cancel_request).
 *
 * @returns {boolean} Whether the cancel went to the queue socket.
 */
export function cancelScheduledRequest() {
    const { requestId } = view;
    if (view.phase !== 'scheduled' || !requestId) {
        return false;
    }
    const previous = view;
    set({ ...view, phase: 'cancelling', problem: undefined });
    waitForAnswer(() => {
        if (view.phase === 'cancelling') {
            set({ ...previous, problem: SCHEDULE_NO_ANSWER });
        }
    });
    queueService.cancelRequest(requestId);
    return true;
}
/** The sheet opened or closed. */
export function noteScheduleSheetOpen(open) {
    sheetOpen = open;
}
/**
 * Report each new problem while the sheet is closed (the answer came after it
 * was put away). Returns an unsubscribe.
 */
export function watchScheduledRequestProblems(onProblem) {
    let last = view.problem;
    return subscribeScheduledRequest(() => {
        const { problem } = view;
        if (problem && problem !== last && !sheetOpen) {
            onProblem(problem);
        }
        last = problem;
    });
}
/** Clear a shown problem (the sheet closed). */
export function clearScheduledRequestProblem() {
    if (view.problem) {
        set({ ...view, problem: undefined });
    }
}
/**
 * An error while a schedule or its cancel is on its way is theirs. Called
 * first by the queue middleware's error listener, which then shows nothing
 * else for it (a refused live request clears the waiting request and fills
 * Home's error line; this one must not).
 *
 * @returns {boolean} Whether the error was the schedule's.
 */
export function isScheduledRequestError(data) {
    if (view.phase === 'scheduling' && data.operation === 'request_interpreter') {
        clearAnswerTimer();
        set({ ...view, phase: view.requestId ? 'scheduled' : 'idle', problem: scheduleRefusalText(data) });
        return true;
    }
    if (view.phase === 'cancelling' && !data.operation && !data.code) {
        clearAnswerTimer();
        // "Queue request not found.": it went live or is gone already.
        if (/not found/i.test(data.message || '')) {
            set({ phase: 'idle', problem: SCHEDULE_GONE });
        }
        else {
            set({ ...view, phase: 'scheduled', problem: SCHEDULE_CANCEL_FAILED });
        }
        return true;
    }
    return false;
}
/**
 * Follow the server's answers. Installed once per sign-in by the queue
 * middleware (clients only).
 */
export function installScheduledRequest() {
    uninstall?.();
    const onScheduled = (data) => {
        if (!data.requestId) {
            return;
        }
        clearAnswerTimer();
        set({ phase: 'scheduled', requestId: data.requestId, scheduledStartAt: data.scheduledStartAt || undefined });
    };
    const onCancelled = (data) => {
        if (data.scheduled !== true || !data.requestId || data.requestId !== view.requestId) {
            return;
        }
        clearAnswerTimer();
        set(data.success === false ? { ...view, phase: 'scheduled', problem: SCHEDULE_CANCEL_FAILED } : IDLE);
    };
    // At its time the server queues it: nothing is scheduled any more.
    const onQueued = (data) => {
        if (data.requestId && data.requestId === view.requestId) {
            clearAnswerTimer();
            set(IDLE);
        }
    };
    queueService.on('requestScheduled', onScheduled);
    queueService.on('requestCancelled', onCancelled);
    queueService.on('requestQueued', onQueued);
    uninstall = () => {
        queueService.off('requestScheduled', onScheduled);
        queueService.off('requestCancelled', onCancelled);
        queueService.off('requestQueued', onQueued);
    };
}
/** Signed out: forget it (the server keeps the request). */
export function resetScheduledRequest() {
    uninstall?.();
    uninstall = undefined;
    clearAnswerTimer();
    set(IDLE);
}
